import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Header from "../../../Checkout/Components/Header/Header";
import Breadcrumbs from "../../../../components/Checkout/Breadcrumbs/BreadCrumbs";
import Footer from "../../../../components/Footer";
import Loader from "../../../../components/Loader/Loader";
import MetaTags from "../../../../components/MetaTags";
import useExperienceData from "../../../../hooks/useExperienceData";
import { experiencesService } from "../../../../services/experiencesService";
import { BookingQuestion } from "../../../../types/bookingQuestions";
import { FormDropdown } from "./FormComponents";
import OrderDetails from "../OrderDetails";
import Question from './Question';
import { useTravelers } from "../../../../context/travelersContext";
import { QuestionAnswers, sortQuestionsByPriority } from "./BookingQuestionsUtils";
import { getAvailableTransferModes, isPickupOfferedByThisProductOption } from "./question-helpers";


const BookingQuestions: React.FC = () => {
    const currentStep = 0;
    const navigate = useNavigate();
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const { promoter, loading, experience } = useExperienceData();
    const { travelers } = useTravelers();
    const travelerPickup = experience?.product.logistics.travelerPickup;

    const [selectedArrivalMode, setSelectedArrivalMode] = useState('');
    const [selectedDepartureMode, setSelectedDepartureMode] = useState('');
    
    const [bookingQuestions, setBookingQuestions] = useState<BookingQuestion[]>([]);
    const [bookingAnswers, setBookingAnswer] = useState<QuestionAnswers>([]);
    const [travelerAnswers, settravelersAnswers] = useState<QuestionAnswers>([]);


    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const totalTravelers = Object.values(travelers).reduce((sum, count) => sum + (count || 0), 0);

    const perTravelerQuestions = sortQuestionsByPriority(
      bookingQuestions?.filter(q => q.group === "PER_TRAVELER") || []
    );
    const perBookingQuestions = sortQuestionsByPriority(bookingQuestions?.filter(q => q.group === "PER_BOOKING") || []);

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                if (experience?.id) {
                    const { bookingQuestions: allQuestions } = await experiencesService.getBookingQuestions();

                    const productQuestions = experience.product.bookingQuestions;
                    const questions = allQuestions.filter(question =>   productQuestions.includes(question.id))
                    setBookingQuestions(questions);
                    
                    
                    // Initialize PER_BOOKING questions
                    const bookingAnswers = questions
                        .filter((q: BookingQuestion) => q.group === "PER_BOOKING")
                        .map((q: BookingQuestion) => {
                
                            const selectedProductOption = experience?.product.productOptions.find(product => product.productOptionCode === selectedItemCode)
                            const pickupOffered = isPickupOfferedByThisProductOption(selectedProductOption, travelerPickup);
                
                            if(q.id === 'PICKUP_POINT' && !pickupOffered) {
                                return  ({
                                    question: 'PICKUP_POINT',
                                    answer: 'MEET_AT_DEPARTURE_POINT',
                                    unit: 'LOCATION_REFERENCE',
                                })
                            }
                
                            return ({
                                question: q.id,
                                answer: ""
                            })
                    });

                    setBookingAnswer(bookingAnswers);

                    if(
                        bookingAnswers.some(answer => answer.question === 'PICKUP_POINT') && 
                        !bookingAnswers.some(answer => answer.question === 'TRANSFER_ARRIVAL_MODE')
                    ) {
                        setSelectedArrivalMode("OTHER");
                    } else {
                        const availableTransferModes = getAvailableTransferModes("TRANSFER_ARRIVAL_MODE", questions, travelerPickup);
                        if(availableTransferModes[0]) {
                            setSelectedArrivalMode(availableTransferModes[0])
                        }
                    }
                        
                    // Initialize PER_TRAVELER questions for each traveler
                    const travelersAnswers: QuestionAnswers = [];
                    for (let i = 0; i < totalTravelers; i++) {
                        questions
                            .filter((q: BookingQuestion) => q.group === "PER_TRAVELER")
                            .forEach((q: BookingQuestion) => {
                                travelersAnswers.push({
                                    question: q.id,
                                    answer: "",
                                    travelerNum: i + 1
                                });
                            });
                    }
                    
                    settravelersAnswers(travelersAnswers);
                }
            } catch (err) {
                console.error("Failed to load booking questions", err);
            } finally {
                setLoadingQuestions(false);
            }
        };

        fetchQuestions();
    }, [experience?.id, totalTravelers, experience?.product.bookingQuestions, experience?.product.productOptions, travelerPickup]);

    useEffect(() => {
        setIsSmallScreen(window.innerWidth <= 768);
        const handleResize = () => setIsSmallScreen(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleBookingQuestionChange = (questionId: string, value: { question: string, answer: string, unit?: string }) => {
        setBookingAnswer(prev => {
            const newAnswers = [...prev];
            const answerIndex = newAnswers.findIndex(a => 
                a.question === questionId
            );

            if (answerIndex !== -1) {
                newAnswers[answerIndex] = {
                    ...newAnswers[answerIndex],
                    answer: value.answer,
                    unit: value.unit
                };
            } else {
                throw(new Error("Internal error state handling questions"))
            }

            return newAnswers;
        });
    };

    const handleQuestionChange = (questionId: string, value: { question: string, answer: string, unit?: string, travelerNum?: number }) => {
        settravelersAnswers(prev => {
            const newAnswers = [...prev];
            const answerIndex = newAnswers.findIndex(a => 
                a.question === questionId && 
                (value.travelerNum !== undefined ? a.travelerNum === value.travelerNum : !a.travelerNum)
            );

            if (answerIndex !== -1) {
                newAnswers[answerIndex] = {
                    ...newAnswers[answerIndex],
                    answer: value.answer,
                    unit: value.unit
                };
            } else {
                throw(new Error("Internal error state handling questions"))
            }

            return newAnswers;
        });
    };

    const handleSubmit = async (event: React.MouseEvent) => {
        event.preventDefault();
        navigate("checkout/passengers");
    };

    if (loading || loadingQuestions || !travelerPickup || !experience) return <Loader/>;

    const currentUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + window.location.search;

    return (
        <HelmetProvider>
            <MetaTags
                name={promoter?.fullname || ""}
                icon={promoter?.logoURL || ""}
                url={currentUrl}
                font={null}
                primaryColor={null}
                secondaryColor={null}
            />
            <Header promoter={promoter || { alias: "", fullname: "", logoURL: "", pictureURL: "", viewMoreURL: "" }}></Header>
            <div className="experiences__passengers__container">
                <Breadcrumbs currentStep={currentStep} orderStatus={null} noGoBack={true}></Breadcrumbs>
                <div className="main-content">
                    <div style={{flex: 'column'}}>
        
                    {/* Per Traveler Questions Section */}
                    {perTravelerQuestions.length > 0 && (
                        <FormDropdown
                            title="Datos de los viajeros"
                        >
                            {Array.from({ length: totalTravelers }).map((_, index: number) => (
                                <div key={`traveler_${index}`} className="experiences__booking__questions__container">
                                    <h3>Viajero {index + 1}</h3>
                                    {perTravelerQuestions.map((question) => (
                                            <Question
                                                itemKey={`${question.id}_traveler_${index}`}
                                                question={question}
                                                questions={bookingQuestions}
                                                travelerPickup={travelerPickup}
                                                selectedArrivalMode={selectedArrivalMode}
                                                selectedDepartureMode={selectedDepartureMode}
                                                setSelectedArrivalMode={setSelectedArrivalMode}
                                                setSelectedDepartureMode={setSelectedDepartureMode}
                                                value={travelerAnswers.find(a => 
                                                    a.question === question.id && 
                                                    a.travelerNum === index + 1
                                                )?.answer || ''}
                                                unit={travelerAnswers.find(a => 
                                                    a.question === question.id && 
                                                    a.travelerNum === index + 1
                                                )?.unit}
                                                onChange={(questionId, value) => handleQuestionChange(questionId, { ...value, travelerNum: index + 1 })}
                                            />
                                    ))}
                                </div>
                            ))}
                        </FormDropdown>
                    )}
                    {/* Per Booking Questions Section */}
                    {perBookingQuestions.length > 0 && (
                        <FormDropdown
                            title="Detalles de la actividad"
                        >
                            <div className="experiences__booking__questions__container">
                                {perBookingQuestions.map((question, index) => (
                                        <Question
                                            itemKey={`${question.id}_booking_${index}`}
                                            question={question}
                                            questions={bookingQuestions}
                                            travelerPickup={travelerPickup}
                                            selectedArrivalMode={selectedArrivalMode}
                                            selectedDepartureMode={selectedDepartureMode}
                                            setSelectedArrivalMode={setSelectedArrivalMode}
                                            setSelectedDepartureMode={setSelectedDepartureMode}
                                            value={bookingAnswers.find(a => a.question === question.id)?.answer || ''}
                                            unit={bookingAnswers.find(a => a.question === question.id)?.unit}
                                            onChange={(questionId, value) => handleBookingQuestionChange(questionId, value)}
                                        />
                                ))}
                            </div>
                        </FormDropdown>
                    )}
                    </div>

                    <OrderDetails
                        handleCheckboxChange={() => { alert("Not implemented")}}
                        handleSubmitNextStep={handleSubmit}
                        isFormValid={true}
                        nextStepLabel={"Siguiente paso"}
                    />
                </div>
                {!isSmallScreen && (
                    <div className="checkout-actions">
                        <button type="button" onClick={handleSubmit}>
                            Siguiente paso
                        </button>
                    </div>
                )}
            </div>
            <Footer promoter={promoter || { alias: "", fullname: "", logoURL: "", pictureURL: "", viewMoreURL: "" }} loading={loading}></Footer>
        </HelmetProvider>
    );
};

export default BookingQuestions;